import React from 'react';
import { useTranslation } from 'react-i18next';
import './ToolsSheet.scss';
import { CloseIcon } from '../common/CloseIcon';

type ToolsSheetProps = {
  open: boolean;
  onClose: () => void;
  /** Opens the Estimate price flow overlay (JAA-285). */
  onEstimate: () => void;
  /** Deep-links to the listing flow that hosts AI photo styling. */
  onStylePhotos: () => void;
  /** Opens the Visibility boost flow overlay (JAA-285). */
  onBoost: () => void;
  /** Mobile tab-slide direction (JAA-266): right on forward, left on back. */
  slideDir?: 'forward' | 'back';
};

// Tool-card glyphs inlined verbatim from the Figma "Tools V2" icon set
// (file 4FpcyaiutzOU5WsPhYu6DG). Inlined rather than <img> so they stay crisp:
// the exported SVGs carry width/height="100%" + preserveAspectRatio="none",
// which stretch and blur when dropped into a fixed-size box.
const EstimateIcon = () => (
  <svg viewBox="0 0 31.4828 30.1505" width="32" height="32" fill="none" aria-hidden="true">
    <path
      d="M14 2.15048C15.3476 2.15048 16.6496 2.34461 17.8828 2.69996C17.3343 3.32354 17.1171 4.03467 17.026 4.37835C16.9559 4.64344 16.9069 4.91544 16.8724 5.18434C15.9551 4.94472 14.9924 4.81715 14 4.81715C7.74077 4.81715 2.66667 9.89126 2.66667 16.1505C2.66689 22.4095 7.74091 27.4838 14 27.4838C20.2591 27.4838 25.3331 22.4095 25.3333 16.1505C25.3333 15.6487 25.2963 15.1546 25.2331 14.67C25.2667 14.6705 25.3001 14.6739 25.3333 14.6739C25.8906 14.6739 26.5135 14.6133 27.1055 14.4565C27.2813 14.4099 27.5522 14.328 27.8594 14.1882C27.9493 14.8295 28 15.4845 28 16.1505C27.9998 23.8823 21.7318 30.1505 14 30.1505C6.26815 30.1505 0.000224459 23.8823 0 16.1505C0 8.4185 6.26801 2.15048 14 2.15048Z"
      fill="#7852E0"
    />
    <path
      d="M12.2721 8.93564C13.6106 8.36674 15.0759 8.33292 16.4349 8.84579C16.9991 9.05875 17.5244 9.35858 18.0026 9.72861C18.1324 9.86135 18.278 9.98947 18.4427 10.1062C18.4535 10.1139 18.4644 10.1219 18.4753 10.1296C18.9427 10.5675 19.354 11.0786 19.6992 11.644C20.0826 12.2724 19.8848 13.0936 19.2565 13.4773C18.6281 13.8608 17.8069 13.6615 17.4232 13.0333C16.9048 12.1843 16.2158 11.6147 15.4935 11.3419C14.7803 11.0727 14.0255 11.0869 13.3151 11.3888C12.6981 11.651 12.1135 12.1335 11.6458 12.8171H14C14.7364 12.8171 15.3333 13.4141 15.3333 14.1505C15.3331 14.8867 14.7362 15.4838 14 15.4838H10.7031C10.6773 15.733 10.6631 15.9863 10.6667 16.2416C10.6694 16.4354 10.6847 16.6275 10.7044 16.8171H14C14.7364 16.8171 15.3333 17.4141 15.3333 18.1505C15.3331 18.8867 14.7362 19.4838 14 19.4838H11.6458C12.1453 20.2135 12.7758 20.7103 13.4349 20.9591C14.148 21.2282 14.903 21.2141 15.6133 20.9122C16.333 20.6062 17.0097 20.0013 17.5065 19.1257C17.8698 18.4853 18.6838 18.2601 19.3242 18.6231C19.9645 18.9865 20.1899 19.8004 19.8268 20.4408C19.081 21.7556 17.9848 22.8005 16.6563 23.3653C15.318 23.9341 13.8523 23.9678 12.4935 23.4552C11.144 22.9459 10.0134 21.9437 9.22786 20.657C9.00102 20.2854 8.80826 19.8913 8.64323 19.4838H7.33333C6.59709 19.4838 6.00022 18.8867 6 18.1505C6 17.4141 6.59695 16.8171 7.33333 16.8171H8.02604C8.01299 16.6386 8.00385 16.459 8.0013 16.2794C7.99756 16.0135 8.00934 15.7479 8.02865 15.4838H7.33333C6.59709 15.4838 6.00022 14.8867 6 14.1505C6 13.4141 6.59695 12.8171 7.33333 12.8171H8.64193C8.7754 12.4878 8.92748 12.167 9.10156 11.8601C9.84749 10.5451 10.9434 9.50049 12.2721 8.93564Z"
      fill="#7852E0"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.7786 0.469492C24.1968 -0.156497 26.4699 -0.156498 26.888 0.469492C27.9466 2.06128 29.4212 3.5372 31.013 4.59579C31.6394 5.0135 31.6393 7.28602 31.013 7.70387L30.4219 8.12053C29.0588 9.1337 27.8143 10.4373 26.888 11.8302C26.4834 12.437 24.3375 12.4551 23.8242 11.8862L23.7786 11.8302C22.72 10.2383 21.2455 8.76247 19.6536 7.70387L19.5977 7.6596C19.0287 7.14625 19.0468 5.00048 19.6536 4.59579C21.2454 3.5372 22.7201 2.06128 23.7786 0.469492ZM25.3333 2.87444C24.3979 4.09075 23.2761 5.21359 22.0599 6.14918C23.2759 7.0845 24.3979 8.20663 25.3333 9.42262C26.2688 8.20663 27.3907 7.0845 28.6068 6.14918C27.3905 5.21359 26.2688 4.09075 25.3333 2.87444Z"
      fill="#7852E0"
    />
  </svg>
);

const StyleIcon = () => (
  <svg viewBox="0 0 32 29.5385" width="32" height="32" fill="none" aria-hidden="true">
    <path
      d="M12.4378 0.00151265H28.0969C29.1707 0.00151265 30.1474 0.452979 30.8538 1.17799C31.5614 1.90433 32 2.90554 32 4.00767V25.5323C32 26.6344 31.5601 27.637 30.8538 28.362L30.8033 28.4098C30.0983 29.1082 29.1435 29.5385 28.0969 29.5385H12.4378C11.5206 29.5385 10.6732 29.2078 10.0043 28.6567L9.17632 28.8851C8.24226 29.1414 7.29008 28.9821 6.50867 28.52L6.4595 28.4881C5.70266 28.022 5.11275 27.2678 4.86823 26.3303L0.11629 8.12786C-0.129517 7.17577 0.0257292 6.20376 0.47464 5.40573C0.924862 4.60237 1.67263 3.97696 2.60669 3.71935L9.10235 1.93205C9.26536 1.65718 9.4607 1.40355 9.68064 1.1778C10.3857 0.450141 11.3625 0 12.4363 0L12.4378 0.00151265ZM10.7909 27.1021C10.8103 27.122 10.8297 27.1419 10.8517 27.1658C11.2566 27.5828 11.8194 27.8404 12.4378 27.8404H28.0969C28.6984 27.8404 29.2457 27.5961 29.648 27.2004L29.683 27.1618C30.0905 26.7436 30.3441 26.1673 30.3441 25.5325V4.00791C30.3441 3.37452 30.0905 2.7969 29.683 2.37861C29.2754 1.96032 28.7127 1.70007 28.0956 1.70007H12.4365C11.8194 1.70007 11.2566 1.96033 10.8491 2.37861C10.4403 2.79425 10.188 3.37186 10.188 4.00791V25.5325C10.188 26.138 10.417 26.6904 10.7883 27.1008L10.7909 27.1021ZM17.3008 7.79484C18.0977 7.79484 18.8209 8.12681 19.3436 8.6646C19.8662 9.20105 20.191 9.94333 20.191 10.7626C20.191 11.5806 19.8675 12.3229 19.3436 12.8594C18.8209 13.3958 18.0977 13.7291 17.3008 13.7291C16.5038 13.7291 15.7807 13.3971 15.2567 12.8594C14.7341 12.3229 14.4093 11.5806 14.4093 10.7626C14.4093 9.94465 14.7327 9.20241 15.2567 8.6646C15.7794 8.12815 16.5026 7.79484 17.3008 7.79484ZM13.4235 14.6918C15.6073 17.1523 18.1715 15.6664 20.5829 14.2682C22.9142 12.9164 25.1355 11.6297 27.1123 12.9217V4.93056H13.4222V14.6918H13.4235ZM12.5956 3.22953H27.9416C28.3983 3.22953 28.7696 3.61063 28.7696 4.07936V21.0839C28.7696 21.5526 28.3983 21.9337 27.9416 21.9337H12.5956C12.1389 21.9337 11.7676 21.5526 11.7676 21.0839V4.07936C11.7676 3.61063 12.1389 3.22953 12.5956 3.22953Z"
      fill="#7852E0"
    />
  </svg>
);

// Placeholder boost glyph: the Figma mock reuses the euro+sparkle icon for the
// Visibility-boost row, so there is no dedicated asset yet. A trending-up arrow
// reads as "more reach" and is swapped for the real icon when boost ships.
const BoostIcon = () => (
  <svg viewBox="0 0 32 32" width="32" height="32" fill="none" aria-hidden="true">
    <path
      d="M4 22L12.5 13.5L17.5 18.5L28 8"
      stroke="#7852E0"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 8H28V15"
      stroke="#7852E0"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * "Jaakko tools" sheet (JAA-266 — Tools tab destination), styled to the Figma
 * "Layouts" tools list. Each row is an icon tile + title + subtitle + pill.
 * Estimate price and Style photos deep-link to existing flows; Visibility boost
 * is a coming-soon stub (boost only exists per-listing post-publish, no global
 * entry yet). Bottom sheet / full page on mobile, centered card on desktop.
 */
const ToolsSheet: React.FC<ToolsSheetProps> = ({
  open,
  onClose,
  onEstimate,
  onStylePhotos,
  onBoost,
  slideDir = 'forward',
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  // Estimate + boost open flow overlays above this sheet (closeOnClick: false)
  // so closing the flow lands the user back on Tools; style navigates away.
  const cards = [
    {
      key: 'estimate',
      onClick: onEstimate,
      closeOnClick: false,
      Glyph: EstimateIcon,
      title: t('newLandingPage.tools.estimate.title'),
      subtitle: t('newLandingPage.tools.estimate.subtitle'),
      tag: t('newLandingPage.tools.estimate.tag'),
      disabled: false,
    },
    {
      key: 'style',
      onClick: onStylePhotos,
      closeOnClick: true,
      Glyph: StyleIcon,
      title: t('newLandingPage.tools.style.title'),
      subtitle: t('newLandingPage.tools.style.subtitle'),
      tag: t('newLandingPage.tools.style.tag'),
      disabled: false,
    },
    {
      key: 'boost',
      onClick: onBoost,
      closeOnClick: false,
      Glyph: BoostIcon,
      title: t('newLandingPage.tools.boost.title'),
      subtitle: t('newLandingPage.tools.boost.subtitle'),
      tag: t('newLandingPage.tools.boost.tag'),
      disabled: false,
    },
  ];

  return (
    <div
      className="tools-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={t('newLandingPage.tools.title')}
    >
      <div className="tools-sheet__backdrop" onClick={onClose} />
      <div className="tools-sheet__panel" data-slide={slideDir}>
        <div className="tools-sheet__header">
          <h2 className="tools-sheet__title">
            {t('newLandingPage.tools.title')}
          </h2>
{/*           <button
            type="button"
            className="tools-sheet__close"
            aria-label={t('newLandingPage.tools.closeAria')}
            onClick={onClose}
          >
            <CloseIcon size={18} color="#131c20" />
          </button> */}
        </div>
        <div className="tools-sheet__cards">
          {cards.map(
            ({ key, onClick, closeOnClick, Glyph, title, subtitle, tag, disabled }) => (
              <button
                key={key}
                type="button"
                className={`tools-sheet__card${disabled ? ' tools-sheet__card--disabled' : ''}`}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onClick();
                  if (closeOnClick) onClose();
                }}
              >
                <span className="tools-sheet__card-icon">
                  <Glyph />
                </span>
                <span className="tools-sheet__card-text">
                  <span className="tools-sheet__card-title">{title}</span>
                  <span className="tools-sheet__card-subtitle">{subtitle}</span>
                  <span
                    className={`tools-sheet__card-tag${disabled ? ' tools-sheet__card-tag--soon' : ''}`}
                  >
                    {tag}
                  </span>
                </span>
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsSheet;
